import { Link, useParams, Navigate } from "react-router-dom";
import {
  HeartHandshake,
  Home as HomeIcon,
  Pill,
  Coffee,
  Brain,
  Sunset,
  Stethoscope,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Phone,
  CheckCircle2,
  Users,
  Target,
  ListChecks,
} from "lucide-react";
import { SITE, SERVICES } from "@/lib/site";

const ICONS = {
  HeartHandshake,
  Home: HomeIcon,
  Pill,
  Coffee,
  Brain,
  Sunset,
  Stethoscope,
  Sparkles,
};

const PANELS = [
  { key: "included", title: "What's included", icon: ListChecks },
  { key: "forWhom", title: "Who it's for", icon: Users },
  { key: "outcomes", title: "The outcomes", icon: Target },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = ICONS[service.icon] || Sparkles;
  const related = SERVICES.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`service-page-${service.slug}`}>
      {/* HERO */}
      <section className="relative bg-brand-cream overflow-hidden">
        <div className="absolute -top-24 -right-32 w-[28rem] h-[28rem] rounded-full bg-brand-soft blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 lg:pt-16 pb-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-up">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition"
            >
              <ArrowLeft className="w-4 h-4" /> All services
            </Link>
            <div className="mt-6 flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-brand-soft flex items-center justify-center">
                <Icon className="w-7 h-7 text-brand" strokeWidth={1.5} />
              </div>
              <span className="text-xs tracking-[0.22em] uppercase font-semibold text-brand">
                Service
              </span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] text-brand-ink">
              {service.title}
            </h1>
            <p className="mt-5 font-display text-xl lg:text-2xl text-brand italic leading-snug max-w-2xl">
              {service.tagline}
            </p>
            <p className="mt-6 text-lg text-brand-mute max-w-2xl leading-relaxed">
              {service.intro}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link
                to="/contact"
                data-testid={`service-cta-enquire-${service.slug}`}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-base font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 shadow-sm"
              >
                Enquire about this service <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href={SITE.phoneHref}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/20 bg-white text-brand-ink px-7 py-4 text-base font-semibold hover:bg-brand-soft transition"
              >
                <Phone className="w-4 h-4" /> Call {SITE.phone}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5 fade-up fade-up-delay-2">
            <div className="relative">
              <div className="absolute -inset-3 bg-brand-soft rounded-[2rem] rotate-[-2deg]" />
              <img
                src={service.hero}
                alt={`${service.title} — Embrace Healthcare`}
                className="relative w-full h-[380px] lg:h-[500px] object-cover rounded-[1.75rem] shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* THREE-COLUMN DETAIL PANELS */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PANELS.map((panel) => (
            <article
              key={panel.key}
              data-testid={`service-panel-${service.slug}-${panel.key}`}
              className="bg-white border border-brand-line rounded-3xl p-8 lg:p-10"
            >
              <div className="w-12 h-12 rounded-2xl bg-brand-soft flex items-center justify-center mb-6">
                <panel.icon
                  className="w-6 h-6 text-brand"
                  strokeWidth={1.5}
                />
              </div>
              <h2 className="font-display text-2xl lg:text-3xl text-brand-ink mb-5">
                {panel.title}
              </h2>
              <ul className="space-y-3">
                {service[panel.key].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-brand-ink"
                  >
                    <CheckCircle2
                      className="w-5 h-5 text-brand mt-1 shrink-0"
                      strokeWidth={1.8}
                    />
                    <span className="text-[15px] leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      {/* RELATED SERVICES */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase font-semibold text-brand">
                <span className="w-8 h-px bg-brand" /> Explore more
              </span>
              <h2 className="font-display text-3xl lg:text-4xl mt-4 leading-tight">
                Related services
              </h2>
            </div>
            <Link
              to="/services"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {related.map((s) => {
              const Cmp = ICONS[s.icon] || Sparkles;
              return (
                <Link
                  to={`/services/${s.slug}`}
                  key={s.slug}
                  data-testid={`service-related-${s.slug}`}
                  className="group bg-white border border-brand-line rounded-3xl p-7 hover:bg-brand hover:text-white transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center mb-5 group-hover:bg-white/15 transition-colors">
                    <Cmp
                      className="w-5 h-5 text-brand group-hover:text-white transition-colors"
                    />
                  </div>
                  <h3 className="font-display text-xl mb-3 group-hover:text-white transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-sm text-brand-mute group-hover:text-white/85 leading-relaxed transition-colors">
                    {s.summary}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-brand-cream border border-brand-line rounded-[2rem] p-10 lg:p-16 overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand-soft blur-3xl pointer-events-none" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">
                  Ready to talk about{" "}
                  <em className="not-italic text-brand">{service.title.toLowerCase()}</em>
                  ?
                </h2>
                <p className="mt-5 text-lg text-brand-mute max-w-2xl">
                  Every care package begins with a friendly conversation and a
                  free assessment — no pressure, no obligation.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3 lg:items-end">
                <a
                  href={SITE.phoneHref}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white px-7 py-4 text-base font-semibold hover:bg-brand-hover transition-all duration-300 hover:-translate-y-0.5 w-full lg:w-auto"
                >
                  <Phone className="w-4 h-4" /> {SITE.phone}
                </a>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-ink/20 bg-white text-brand-ink px-7 py-4 text-base font-semibold hover:bg-brand-soft transition w-full lg:w-auto"
                >
                  Send an enquiry <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
